import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@headlessui/react';
import {
  ArrowLeftStartOnRectangleIcon,
  ClipboardIcon,
  UserCircleIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  BuildingStorefrontIcon,
  GlobeAltIcon,
  ChevronRightIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import useAuthValidation from '../hooks/useAuthValidation';
import LanguageSwitcher from './shared/LanguageSwitcher';
import BrandLogo from './shared/BrandLogo';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = useAuthValidation();
  const { t } = useTranslation();

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/auth');
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const NavLink = ({ to, icon: Icon, label, indent = false }: { to: string; icon: any; label: string; indent?: boolean }) => {
    const active = isActive(to);
    return (
      <Link
        to={to}
        className={`
          group flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200
          ${indent ? 'ml-4' : ''}
          ${active
            ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30'
            : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
          }
        `}
      >
        <Icon className={`size-5 ${active ? 'text-white' : 'text-slate-400 group-hover:text-white'} transition-colors`} />
        <span className="flex-1">{label}</span>
        {active && <ChevronRightIcon className="size-4" />}
      </Link>
    );
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Sidebar */}
      <div className="w-72 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 shadow-2xl border-r border-slate-700/50 h-screen fixed">
        <div className="flex flex-col h-full p-6 overflow-y-auto">
          {/* Logo */}
          <div className="mb-10 flex justify-center">
            <BrandLogo className="h-14 w-auto drop-shadow-lg" variant="light" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1">
            {role === 'merchant' && (
              <>
                <NavLink to="/merchant" icon={ClipboardIcon} label={t('navigation.overview')} />
                <NavLink to="/add-precheck-pos" icon={CheckCircleIcon} label={t('navigation.pos')} />
                <NavLink to="/add-precheck" icon={CheckCircleIcon} label={t('navigation.ecommerce')} />
              </>
            )}

            {(role === 'admin' || role === 'domain_admin') && (
              <>
                <NavLink to="/dashboard" icon={ClipboardIcon} label={t('navigation.dashboard')} />
                {role === 'admin' && (
                  <>
                    <NavLink to="/users" icon={UserCircleIcon} label={t('navigation.users')} />
                    <NavLink to="/domain-configs" icon={GlobeAltIcon} label={t('navigation.domains')} />
                  </>
                )}
                <NavLink to="/merchants" icon={BuildingStorefrontIcon} label={t('navigation.merchants')} />
                <NavLink to="/api-keys" icon={KeyIcon} label={t('navigation.api')} />

                {/* Prechecks Section */}
                <div className="pt-4">
                  <div className="flex items-center gap-2 px-4 py-2 text-slate-400 text-sm font-semibold uppercase tracking-wider">
                    <ClipboardDocumentCheckIcon className="size-4" />
                    <span>{t('navigation.prechecks')}</span>
                  </div>
                  <div className="mt-1 space-y-1">
                    <NavLink to="/precheck-pos" icon={CheckCircleIcon} label={t('navigation.pos')} indent />
                    <NavLink to="/precheck" icon={CheckCircleIcon} label={t('navigation.ecommerce')} indent />
                  </div>
                </div>
              </>
            )}
          </nav>

          {/* Logout Button */}
          <div className="pt-6 mt-6 border-t border-slate-700/50">
            <Button
              onClick={handleLogout}
              className="group flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-red-500/10 hover:text-red-400 font-medium transition-all duration-200"
            >
              <ArrowLeftStartOnRectangleIcon className="size-5 text-slate-400 group-hover:text-red-400 transition-colors" />
              <span>{t('navigation.signOut')}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col ml-72">
        {/* Language Switcher Row */}
        <div className="flex justify-end px-8 pt-6 pb-2">
          <LanguageSwitcher />
        </div>

        {/* Page Content */}
        <main className="flex-1 px-8 pb-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
